import {
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Flex,
  Radio,
  RadioGroup,
  useToast,
} from "@daangn/carotene";
import { atoms } from "@daangn/carotene/css";
import { IconMegaphoneFill } from "@daangn/react-monochrome-icon";
import {
  CommonSearchParamKey,
  KarrotLocalCountryCode,
  buildRegionParamValue,
} from "@karrotmarket-com/core";
import { useLocation } from "@remix-run/react";
import { useState } from "react";
import { match } from "ts-pattern";
import { GoogleCircle, GoogleMap } from "~/services/GoogleMap";
import { findRegionByLocationCurrent } from "~/utils/getCurrentUserLocation";
import { services } from "~/utils/services";
import { useNormalizeHref } from "~/utils/useNormalizeHref";
import * as css from "./NeighbourhoodModal.css";

const karrotmarketApiService = services.getInstance().karrotmarket;

export type User = {
  id: string;
  nickname: string;
  isPersonaVerified: boolean;
  score: number;
  scoreSymbolImageUrl: string;
  userLocations: {
    h3Index: number;
    name: string;
    current: boolean;
    checkinCount: number;
    rangeLevel: number;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    countryCode: string;
  }[];
  userRegions: {
    id: number;
    name: string;
    current: boolean;
    checkinCount: number;
    regionRange: string;
    countryCode: string;
  }[];
  profileImageUrl: string;
  reviewCount: number;
};

const meterToKilometer = (meter: number) => {
  return meter / 1000;
};

/**
 * @description 범위 레벨에 따라 지도 원의 반지름(m)과 zoom 값을 반환하는 함수
 */
function getCircleConfig(rangeLevel: number) {
  return match(rangeLevel)
    .with(1, () => ({ radius: 1000, zoom: 13 }))
    .with(2, () => ({ radius: 2500, zoom: 12 }))
    .with(3, () => ({ radius: 5000, zoom: 11 }))
    .with(4, () => ({ radius: 12500, zoom: 10 }))
    .with(5, () => ({ radius: 25000, zoom: 9 }))
    .otherwise(() => ({ radius: 12500, zoom: 10 }));
}

const NeighbourhoodModal = ({
  user,
  currentUserLocation,
  trigger,
  open,
  onOpenChange,
}: {
  user: User;
  currentUserLocation: {
    userLocation: User["userLocations"][number];
    userRegion: User["userRegions"][number];
  };
  trigger: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const location = useLocation();
  const { toast } = useToast();

  const { navigateToNormalizedUrl } = useNormalizeHref();

  const [center, setCenter] = useState<{
    lat: number;
    lng: number;
  }>({
    lat: currentUserLocation.userLocation.coordinates.latitude,
    lng: currentUserLocation.userLocation.coordinates.longitude,
  });

  const [selectedLocation, setSelectedLocation] = useState(
    currentUserLocation.userLocation,
  );
  const [selectedRegion, setSelectedRegion] = useState(
    currentUserLocation.userRegion,
  );

  const { radius, zoom } = getCircleConfig(selectedLocation.rangeLevel);

  const handleClick = async () => {
    if (selectedLocation.h3Index === currentUserLocation.userLocation.h3Index) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set(
      CommonSearchParamKey.In,
      buildRegionParamValue({
        region: {
          id: selectedRegion.id.toString(),
          name: selectedRegion.name,
        },
      }),
    );

    try {
      await karrotmarketApiService.setLocation({
        karrotLocalCountryCode: KarrotLocalCountryCode.CA,
        location: {
          h3Index: selectedLocation.h3Index,
          locationName: selectedLocation.name,
          regionId: selectedRegion.id,
          regionName: selectedRegion.name,
          countryCode: selectedRegion.countryCode.toLowerCase(),
        },
      });

      onOpenChange(false);
      navigateToNormalizedUrl({
        pathname: `/${selectedRegion.countryCode.toLowerCase()}/`,
        queryString: `?${searchParams.toString()}`,
      });
    } catch (error) {
      console.error(error);
      toast.warning("Failed to change location. Please try again later.", {});
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);

        if (!open) {
          setSelectedLocation(currentUserLocation.userLocation);
          setSelectedRegion(currentUserLocation.userRegion);
          setCenter({
            lat: currentUserLocation.userLocation.coordinates.latitude,
            lng: currentUserLocation.userLocation.coordinates.longitude,
          });
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        position={{
          base: "bottom",
          medium: "center",
        }}
      >
        <DialogHeader className={css.dialogHeader}>
          <DialogTitle>Manage neighbourhood</DialogTitle>
        </DialogHeader>
        <Flex flexDirection="column" gap={6} paddingX={5} paddingY={6}>
          <Flex
            justifyContent="center"
            alignItems="center"
            className={css.mapWrapper}
          >
            <GoogleMap center={center} zoom={zoom} gestureHandling="none">
              <GoogleCircle
                radius={radius}
                center={center}
                strokeColor={"#f60"}
                strokeWeight={1}
              />
            </GoogleMap>
          </Flex>
          <Flex width="full" flexDirection="column" gap={3}>
            <label className={css.label} htmlFor="neighbourhood">
              Showing listing within {meterToKilometer(radius * 2)}km of{" "}
              {selectedLocation.name}
            </label>
            <RadioGroup
              id="neighbourhood"
              value={selectedLocation.name}
              onValueChange={(value) => {
                const foundLocationIndex = user.userLocations.findIndex(
                  (location) => location.name === value,
                );
                const foundLocation = user.userLocations[foundLocationIndex];
                const foundRegion = findRegionByLocationCurrent(
                  foundLocation.current,
                  user.userRegions,
                );

                setSelectedLocation(foundLocation);
                setCenter({
                  lat: foundLocation.coordinates.latitude,
                  lng: foundLocation.coordinates.longitude,
                });

                setSelectedRegion(foundRegion);
              }}
            >
              {user.userLocations.map((location) => (
                <Radio
                  key={location.h3Index}
                  label={location.name}
                  value={location.name}
                />
              ))}
            </RadioGroup>
          </Flex>

          <Callout variant="warning" icon={<IconMegaphoneFill size={16} />}>
            Adding or deleting your neighbourhood is only possible through the
            mobile app. Only switching between two neighbourhoods that you have
            previously added on the mobile app is possible on the website.
          </Callout>

          <Flex width="full">
            <Button
              variant="brand"
              size="xlarge"
              className={atoms({
                width: "full",
              })}
              onClick={handleClick}
            >
              Done
            </Button>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default NeighbourhoodModal;
